import type { DeckProject, FontStyle, FontWeight, ProjectFontFace, ProjectFontFamily } from './types'

export const FONT_WEIGHTS: FontWeight[] = [100, 200, 300, 400, 500, 600, 700, 800, 900]

export const DEVICE_FONTS = ['Arial', 'Georgia', 'Verdana', 'Trebuchet MS', 'Courier New'] as const

const PROJECT_FONT_PREFIX = 'project-font:'
const registeredFaces = new Map<string, { face: FontFace; signature: string }>()
const loadingFaces = new Map<string, { signature: string; promise: Promise<void> }>()

const weightNames: Record<FontWeight, string> = {
  100: 'Thin',
  200: 'Extra light',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'Semi bold',
  700: 'Bold',
  800: 'Extra bold',
  900: 'Black',
}

export const fontWeightLabel = (weight: FontWeight) => `${weight} · ${weightNames[weight]}`

export const normalizeFontWeight = (value: unknown): FontWeight => {
  if (value === 'bold') return 700
  if (value === 'normal') return 400
  const number = Number(value)
  return FONT_WEIGHTS.reduce((closest, weight) =>
    Math.abs(weight - number) < Math.abs(closest - number) ? weight : closest, 400)
}

export const projectFontValue = (familyId: string) => `${PROJECT_FONT_PREFIX}${familyId}`

export const projectFontId = (value: unknown) => {
  const text = String(value ?? '')
  return text.startsWith(PROJECT_FONT_PREFIX) ? text.slice(PROJECT_FONT_PREFIX.length) : undefined
}

export const projectFontFamily = (fonts: Record<string, ProjectFontFamily>, value: unknown) => {
  const id = projectFontId(value)
  return id ? fonts[id] : undefined
}

const cssName = (familyId: string) => `Deckelier_${familyId.replace(/[^a-z0-9_-]/gi, '_')}`

export const cssFontFamily = (fonts: Record<string, ProjectFontFamily>, value: unknown) => {
  const family = projectFontFamily(fonts, value)
  return family ? `"${cssName(family.id)}"` : String(value || 'Arial')
}

export const fontRenderSpec = (
  fonts: Record<string, ProjectFontFamily>,
  familyValue: unknown,
  weight: unknown,
  style: FontStyle,
) => ({
  family: cssFontFamily(fonts, familyValue),
  weight: normalizeFontWeight(weight),
  style,
})

export const konvaFontProps = (
  fonts: Record<string, ProjectFontFamily>,
  familyValue: unknown,
  weight: unknown,
  style: FontStyle,
) => {
  const spec = fontRenderSpec(fonts, familyValue, weight, style)
  return { fontFamily: spec.family, fontStyle: `${spec.style} ${spec.weight}` }
}

export const canvasFontDeclaration = (
  fonts: Record<string, ProjectFontFamily>,
  familyValue: unknown,
  weight: unknown,
  style: FontStyle,
  sizePx: number,
) => {
  const spec = fontRenderSpec(fonts, familyValue, weight, style)
  return `${spec.style} ${spec.weight} ${sizePx}px ${spec.family}, sans-serif`
}

export const availableFontStyles = (family?: ProjectFontFamily): FontStyle[] => {
  if (!family) return ['normal', 'italic']
  const styles = (['normal', 'italic'] as FontStyle[]).filter((style) => family.faces.some((face) => face.style === style))
  return styles.length ? styles : ['normal']
}

export const availableFontWeights = (family: ProjectFontFamily | undefined, style: FontStyle): FontWeight[] => {
  if (!family) return [400, 700]
  const weights = FONT_WEIGHTS.filter((weight) => family.faces.some((face) => face.style === style && face.weight === weight))
  return weights.length ? weights : FONT_WEIGHTS.filter((weight) => family.faces.some((face) => face.weight === weight))
}

export const closestFontFace = (family: ProjectFontFamily, style: FontStyle, weight: unknown) => {
  const preferredStyle = family.faces.filter((face) => face.style === style)
  const candidates = preferredStyle.length ? preferredStyle : family.faces
  const target = normalizeFontWeight(weight)
  return [...candidates].sort((a, b) => Math.abs(a.weight - target) - Math.abs(b.weight - target))[0]
}

const tokenWeight = (token: string): FontWeight | undefined => {
  const normalized = token.toLowerCase().replace(/[^a-z0-9]/g, '')
  const named: Record<string, FontWeight> = {
    thin: 100,
    hairline: 100,
    extralight: 200,
    ultralight: 200,
    light: 300,
    regular: 400,
    normal: 400,
    book: 400,
    medium: 500,
    semibold: 600,
    demibold: 600,
    bold: 700,
    extrabold: 800,
    ultrabold: 800,
    black: 900,
    heavy: 900,
  }
  if (named[normalized]) return named[normalized]
  const numeric = normalized.match(/(?:^|wght)([1-9]00)$/)?.[1]
  return numeric ? normalizeFontWeight(Number(numeric)) : undefined
}

export interface SuggestedFontFace {
  family: string
  weight: FontWeight
  style: FontStyle
}

export const suggestFontFace = (fileName: string): SuggestedFontFace => {
  const base = fileName.replace(/\.(woff2?|ttf|otf)$/i, '')
  const expanded = base
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b(extra|ultra)\s+(light|bold)\b/gi, '$1$2')
    .replace(/\b(semi|demi)\s+bold\b/gi, '$1bold')
  const words = expanded.split(/[\s_-]+/).filter(Boolean)
  let weight: FontWeight = 400
  let style: FontStyle = 'normal'
  const familyWords = words.filter((word) => {
    const normalized = word.toLowerCase().replace(/[^a-z0-9]/g, '')
    if (normalized === 'italic' || normalized === 'oblique') { style = 'italic'; return false }
    const detectedWeight = tokenWeight(normalized.replace(/italic|oblique/g, ''))
    if (detectedWeight) { weight = detectedWeight; if (/italic|oblique/.test(normalized)) style = 'italic'; return false }
    return true
  })
  return {
    family: familyWords.join(' ').replace(/\s+/g, ' ').trim() || 'Uploaded font',
    weight,
    style,
  }
}

export const fontFaceKey = (familyName: string, face: Pick<ProjectFontFace, 'weight' | 'style'>) =>
  `${familyName.trim().toLocaleLowerCase()}|${face.weight}|${face.style}`

const faceSignature = (face: ProjectFontFace) => `${face.dataUrl.length}:${face.dataUrl.slice(-32)}:${face.weight}:${face.style}`

const createBrowserFontFace = (family: ProjectFontFamily, face: ProjectFontFace) =>
  new FontFace(cssName(family.id), `url("${face.dataUrl}")`, {
    weight: String(face.weight),
    style: face.style,
  })

export const validateFontData = async (familyName: string, face: ProjectFontFace) => {
  if (typeof FontFace === 'undefined') throw new Error('This browser does not support custom fonts.')
  const candidateFamily: ProjectFontFamily = { id: `validation-${face.id}`, name: familyName, faces: [face] }
  await createBrowserFontFace(candidateFamily, face).load()
}

export interface FontLoadResult {
  loaded: string[]
  failed: Array<{ faceId: string; message: string }>
}

export const loadProjectFonts = async (fonts: Record<string, ProjectFontFamily>): Promise<FontLoadResult> => {
  if (typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts) {
    return { loaded: [], failed: Object.values(fonts).flatMap((family) => family.faces.map((face) => ({ faceId: face.id, message: 'Custom fonts are not supported in this browser.' }))) }
  }
  const currentIds = new Set(Object.values(fonts).flatMap((family) => family.faces.map((face) => face.id)))
  registeredFaces.forEach((registered, id) => {
    if (!currentIds.has(id)) { document.fonts.delete(registered.face); registeredFaces.delete(id) }
  })
  const loaded: string[] = []
  const failed: FontLoadResult['failed'] = []
  await Promise.all(Object.values(fonts).flatMap((family) => family.faces.map(async (face) => {
    const signature = faceSignature(face)
    const existing = registeredFaces.get(face.id)
    if (existing?.signature === signature) { loaded.push(face.id); return }
    if (existing) document.fonts.delete(existing.face)
    const inFlight = loadingFaces.get(face.id)
    const promise = inFlight?.signature === signature
      ? inFlight.promise
      : (async () => {
          const browserFace = await createBrowserFontFace(family, face).load()
          document.fonts.add(browserFace)
          registeredFaces.set(face.id, { face: browserFace, signature })
        })()
    if (!inFlight || inFlight.signature !== signature) loadingFaces.set(face.id, { signature, promise })
    try {
      await promise
      loaded.push(face.id)
    } catch (error) {
      registeredFaces.delete(face.id)
      failed.push({ faceId: face.id, message: error instanceof Error ? error.message : 'The font file could not be loaded.' })
    } finally {
      if (loadingFaces.get(face.id)?.promise === promise) loadingFaces.delete(face.id)
    }
  })))
  await document.fonts.ready
  return { loaded, failed }
}

export const fontFamilyUsageCount = (project: DeckProject, familyId: string) => {
  const value = projectFontValue(familyId)
  let count = project.elements.filter((element) => element.type === 'text' && element.fontFamily === value).length
  const variableIds = new Set(project.elements.map((element) => element.bindings.fontFamily).filter(Boolean))
  project.variables.filter((variable) => variableIds.has(variable.id)).forEach((variable) => {
    if (variable.defaultValue === value) count += 1
    count += project.rows.filter((row) => row.values[variable.id] === value).length
  })
  return count
}
